import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { ReactNode } from 'react';

import { Sizes, Spacing, Typography } from 'chatie-design-system';
import { CheckCircle, Icon, Tap } from 'chatie-design-system/icon';

import Badge, { useBadgePresets } from '@components/common/Badge';
import RoundAvatar from '@components/common/RoundAvatar';

import { useGlobalContext } from '@contexts/global';
import { IEFinderObject, IPeekInfo } from '@model';
import { formatNumberMillion } from '@utils/chat';
import { differenceInDays, formatDate } from '@utils/date';

export type ChatRoomInfo = {
  characterName: string;
  image?: IEFinderObject<'image'>;
  lastMessage: string;
  lastMessageDate?: string;
  id: number;
  peek?: IPeekInfo;
};

const chatRoomCSS = (isBorder: boolean) => (theme: Theme) =>
  css`
    display: flex;
    padding: ${Spacing.small}px ${Spacing.small}px ${Spacing.small}px
      ${Spacing.medium}px;
    height: 68px;
    ${isBorder ? `border: 1px solid ${theme.colors.main500}` : null};
    ${isBorder ? `border-radius: 5px` : null};
    color: ${theme.var.gray900};
    overflow: visible;
    position: relative;
  `;

const badgeCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  bottom: 8px;

  span {
    height: 16px;
    line-height: 16px;
  }
`;

const adultBadgeCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  bottom: 8px;
  left: 21px;
  padding: 0 2px;

  span {
    height: 16px;
    line-height: 16px;
  }
`;
const chatRoomHeaderCSS = (isEdit: boolean) => (theme: Theme) =>
  css`
    display: flex;
    flex-direction: ${isEdit ? 'row' : 'column'};
    margin-left: ${Spacing.medium}px;
    width: 100%;
    justify-content: ${isEdit ? 'space-between' : 'center'};
    ${isEdit ? 'align-items: center' : 'null'};

    p {
      ${Typography.body12};
      color: ${theme.var.gray500};
      line-height: 135%;
    }

    .date {
      text-align: end;
      width: 70px;
      min-width: 70px;
      max-width: 70px;
    }

    .character-name {
      display: flex;
      text-align: left;
      align-item: center;
      line-height: 135%;
      gap: ${Spacing.xhsmall}px;

      .new-badge {
        display: flex;
        align-items: center;
      }
    }

    .last-message {
      text-align: left;
    }
  `;

const chatRoomTopHeaderCSS = css`
  display: flex;
  text-align: center;
  justify-content: space-between;

  h5 {
    ${Typography.h5};
    align-self: start;
    line-height: 100%;
  }

  .character-name {
    display: flex;
  }
`;
const chatRoomBottomHeaderCSS = css`
  margin-top: ${Spacing.xxsmall}px;
  align-self: start;
`;
const storySelecButtonCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.var.black};
  top: 50%;
`;

const statCSS = (theme: Theme) => css`
  display: flex;
  margin-top: 6px;
  gap: 10px;

  .tab-container,
  .balloon-container {
    display: flex;
    flex-direction: row;
    gap: 2px;
    align-items: center;
    text-align: center;

    & p {
      ${Typography.body12};
    }

    .tab {
      color: ${theme.colors.main500};
    }
    .balloon {
      color: ${theme.var.gray500};
    }
  }
`;

export type ChatRoomProps = {
  chatRoomInfo: ChatRoomInfo;
  isEdit?: boolean;
  isCheck?: boolean;
  isBorder?: boolean;
  right?: ReactNode;
  onClickAvatar?: () => void;
  isNewCharacter?: boolean;
  isDeletedCharacter?: boolean;
  isMyCharacter?: boolean;
  isAdultChatRoom?: boolean;
};

const ChatRoom = ({
  chatRoomInfo,
  isEdit = false,
  isCheck = false,
  isBorder = false,
  right,
  onClickAvatar,
  isNewCharacter = false,
  isDeletedCharacter,
  isMyCharacter,
  isAdultChatRoom,
}: ChatRoomProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mainFill } = useBadgePresets();
  const { isCheming } = useGlobalContext();

  const getFormattedDate = (lastMessageDate: string): string => {
    const koreanDate = new Date(lastMessageDate);
    const currentKoreanDate = new Date();
    const currentKoreanDateOnly = new Date(
      currentKoreanDate.setHours(0, 0, 0, 0)
    );
    const koreanDateOnly = new Date(koreanDate.setHours(0, 0, 0, 0));

    const diff = differenceInDays(currentKoreanDateOnly, koreanDateOnly);

    if (diff < 1) {
      return formatDate(lastMessageDate, 'a h:mm');
    } else if (diff >= 1 && diff < 2) {
      return '어제';
    } else {
      return `${diff}일 전`;
    }
  };

  const lastMessageDate = chatRoomInfo?.lastMessageDate ?? '';
  const parsedLastMessageDate = getFormattedDate(lastMessageDate);

  const hasPeek = chatRoomInfo?.peek?.status === 'AT';
  return (
    <>
      <article css={chatRoomCSS(isBorder)} className="chat-room-container">
        <div onClick={() => (onClickAvatar ? onClickAvatar() : null)}>
          <RoundAvatar size={44} picture={chatRoomInfo.image} />
          {isMyCharacter && !isAdultChatRoom && (
            <Badge text={t('내 캐릭터')} {...mainFill} css={badgeCSS} />
          )}
          {isAdultChatRoom && (
            <Badge text={t('🤫 쉿')} {...mainFill} css={adultBadgeCSS} />
          )}
        </div>
        <div
          css={chatRoomHeaderCSS(!!isEdit || !!right)}
          className={'chat-room-header'}
        >
          {!right && !isEdit && (
            <div onClick={() => (onClickAvatar ? onClickAvatar() : null)}>
              <div css={chatRoomTopHeaderCSS}>
                <div className="character-name">
                  <h5>
                    {t('{{characterName}}', {
                      characterName: isDeletedCharacter
                        ? '알 수 없음'
                        : chatRoomInfo.characterName,
                    })}
                  </h5>
                  {isNewCharacter && (
                    <Badge
                      text={'새 캐릭터 등장 👀'}
                      backgroundColor={
                        isCheming
                          ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                          : 'linear-gradient(90deg, #04C8D0, #1590D5)'
                      }
                      fontSize={10}
                      className={'new-badge'}
                    />
                  )}
                </div>
                <p className="date">
                  {t('{{lastMessageDate}}', {
                    lastMessageDate: parsedLastMessageDate,
                  })}
                </p>
              </div>
              <div css={chatRoomBottomHeaderCSS}>
                <p className="last-message">
                  {t('{{lastMessage}}', {
                    lastMessage: isDeletedCharacter
                      ? '삭제된 캐릭터 입니다.'
                      : chatRoomInfo.lastMessage,
                  })}
                </p>
              </div>
              {!!hasPeek && (
                <div className="stat-container" css={statCSS}>
                  <div className="tab-container">
                    <Icon
                      icon={Tap}
                      size={Sizes.xsmall}
                      color={theme.colors.main500}
                    />
                    <p className="tab">
                      {t(
                        formatNumberMillion(chatRoomInfo?.peek?.tap_count ?? 0)
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          {(isEdit || right) && (
            <>
              <div onClick={() => (onClickAvatar ? onClickAvatar() : null)}>
                <div css={chatRoomTopHeaderCSS}>
                  <div className="character-name">
                    <h5>
                      {t('{{characterName}}', {
                        characterName: isDeletedCharacter
                          ? '알 수 없음'
                          : chatRoomInfo.characterName,
                      })}
                    </h5>
                    {isNewCharacter && (
                      <Badge
                        text={'새 캐릭터 등장 👀'}
                        backgroundColor={
                          isCheming
                            ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                            : 'linear-gradient(90deg, #04C8D0, #1590D5)'
                        }
                        fontSize={10}
                        className={'new-badge'}
                      />
                    )}
                  </div>
                </div>
                <div css={chatRoomBottomHeaderCSS}>
                  <p className="last-message">
                    {t('{{lastMessage}}', {
                      lastMessage: isDeletedCharacter
                        ? '삭제된 캐릭터 입니다.'
                        : chatRoomInfo.lastMessage,
                    })}
                  </p>
                </div>
                {hasPeek && (
                  <div className="stat-container" css={statCSS}>
                    <div className="tab-container">
                      <Icon
                        icon={Tap}
                        size={Sizes.xsmall}
                        color={theme.colors.main500}
                      />
                      <p className="tab">
                        {t(
                          formatNumberMillion(
                            chatRoomInfo?.peek?.tap_count ?? 0
                          )
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {right ?? (
                <button css={storySelecButtonCSS}>
                  <Icon
                    icon={CheckCircle}
                    size={Sizes.large}
                    color={isCheck ? theme.colors.main500 : theme.var.gray200}
                  />
                </button>
              )}
            </>
          )}
        </div>
      </article>
    </>
  );
};

export default ChatRoom;
